/**
 * The four-column route paradigm, as pure functions.
 *
 * Every place Nightshift chooses a model uses the same ordered list of rows, and every row is
 * Machine → Harness → Model → Thinking, each dropdown narrowed by the ones to its left:
 *
 *   - machine: the registry (or "any machine");
 *   - harness: the harnesses that machine reports installed, out of those the catalogue knows;
 *   - model: the catalogue entries for that harness that the machine reported (`availableOn`),
 *     or every entry for the harness when the row is not machine-specific;
 *   - thinking: the levels that model accepts, or none.
 *
 * The component in `components/route-list-editor.tsx` renders these; the arithmetic lives
 * here so the narrowing can be tested without a DOM and cannot drift between surfaces.
 */
import type { UiCatalogueModel } from '@nightshift/ui-types';

export interface RouteRow {
  /** Null means the row is not machine-specific. */
  machine: string | null;
  harness: string;
  model: string;
  thinking: string | null;
}

export interface RouteMachine {
  name: string;
  installedHarnesses: string[];
  live: boolean;
  /** Paused by an operator: still selectable, but the option says so. */
  paused?: boolean;
}

/** The option text for a machine: its name plus the one word that says why it might not run. */
export function machineOptionLabel(machine: RouteMachine): string {
  if (machine.paused) return `${machine.name} (paused)`;
  return machine.live ? machine.name : `${machine.name} (offline)`;
}

const HARNESS_LABELS: Record<string, string> = {
  claude: 'Claude Code',
  codex: 'Codex',
  cursor: 'Cursor',
  kimi: 'Kimi',
};

export function harnessLabel(harness: string): string {
  return HARNESS_LABELS[harness] ?? harness;
}

function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
}

export function findMachine(machines: readonly RouteMachine[], name: string | null): RouteMachine | undefined {
  if (!name) return undefined;
  return machines.find((machine) => sameName(machine.name, name));
}

/**
 * Whether the machine reports this harness installed. A harness can be installed and still
 * missing from the catalogue: its model probe failed, or the operator switched the vendor off.
 */
export function harnessInstalledOn(machine: string | null, harness: string, machines: readonly RouteMachine[]): boolean {
  const found = findMachine(machines, machine);
  return found !== undefined && found.installedHarnesses.some((name) => name.toLowerCase() === harness);
}

/** Harnesses a row may name: the catalogue's, narrowed to what the chosen machine reports installed. */
export function harnessesFor(
  machine: string | null,
  machines: readonly RouteMachine[],
  models: readonly UiCatalogueModel[],
): string[] {
  const all = [...new Set(models.map((model) => model.harness as string))].sort();
  const found = findMachine(machines, machine);
  if (!found || found.installedHarnesses.length === 0) return all;
  const installed = new Set(found.installedHarnesses.map((name) => name.toLowerCase()));
  return all.filter((harness) => installed.has(harness));
}

/** Catalogue entries for one harness that the chosen machine can run. */
export function modelsFor(
  machine: string | null,
  harness: string,
  models: readonly UiCatalogueModel[],
): UiCatalogueModel[] {
  return models.filter((model) => {
    if (model.harness !== harness) return false;
    if (harness === 'codex' && (model.source !== 'machine' || model.availableOn.length === 0)) return false;
    if (machine === null || model.availableOn.length === 0) return true;
    return model.availableOn.some((name) => sameName(name, machine));
  });
}

function catalogueEntry(
  harness: string,
  model: string,
  models: readonly UiCatalogueModel[],
): UiCatalogueModel | undefined {
  return models.find((entry) => entry.harness === harness && entry.id === model);
}

/** Thinking levels a model accepts; empty when it has none. */
export function effortsFor(harness: string, model: string, models: readonly UiCatalogueModel[]): string[] {
  return catalogueEntry(harness, model, models)?.efforts ?? [];
}

/** The level a fresh pick of this model starts on: the source default, else its first level, else none. */
export function defaultThinkingFor(
  harness: string,
  model: string,
  models: readonly UiCatalogueModel[],
): string | null {
  const entry = catalogueEntry(harness, model, models);
  if (!entry) return null;
  if (entry.defaultEffort && entry.efforts.includes(entry.defaultEffort)) return entry.defaultEffort;
  return entry.efforts[0] ?? null;
}

/**
 * A row whose model is not in the catalogue for its harness, or whose thinking level the
 * model does not list. Saved rows can be like this after a machine goes away or a provider
 * retires a family; they stay selectable so the operator sees what is stored.
 */
export function rowProblem(
  row: RouteRow,
  machines: readonly RouteMachine[],
  models: readonly UiCatalogueModel[],
): string | null {
  if (row.machine && !findMachine(machines, row.machine)) return `${row.machine} is not a registered machine`;
  if (!harnessesFor(row.machine, machines, models).includes(row.harness)) {
    if (!row.machine) return `${harnessLabel(row.harness)} is not in the catalogue`;
    return harnessInstalledOn(row.machine, row.harness, machines)
      ? `No ${harnessLabel(row.harness)} models reported by ${row.machine}`
      : `${harnessLabel(row.harness)} is not installed on ${row.machine}`;
  }
  if (!modelsFor(row.machine, row.harness, models).some((model) => model.id === row.model)) {
    return row.machine
      ? `${row.model} is not reported by ${row.machine}`
      : `${row.model} is not in the catalogue`;
  }
  const efforts = effortsFor(row.harness, row.model, models);
  if (row.thinking && !efforts.includes(row.thinking)) return `${row.model} does not offer ${row.thinking} thinking`;
  return null;
}

function firstViable(
  machine: string | null,
  machines: readonly RouteMachine[],
  models: readonly UiCatalogueModel[],
  preferHarness?: string,
  preferModel?: string,
): Pick<RouteRow, 'harness' | 'model' | 'thinking'> | undefined {
  const harnesses = harnessesFor(machine, machines, models);
  const harness = preferHarness && harnesses.includes(preferHarness) ? preferHarness : harnesses[0];
  if (!harness) return undefined;
  const options = modelsFor(machine, harness, models);
  const model = preferModel && options.some((entry) => entry.id === preferModel) ? preferModel : options[0]?.id;
  if (!model) return undefined;
  return { harness, model, thinking: defaultThinkingFor(harness, model, models) };
}

/** Re-narrow a row after its machine changed; keep the harness and model when they still fit. */
export function rowAfterMachineChange(
  row: RouteRow,
  machine: string | null,
  machines: readonly RouteMachine[],
  models: readonly UiCatalogueModel[],
): RouteRow {
  const next = firstViable(machine, machines, models, row.harness, row.model);
  if (!next) return { ...row, machine };
  const keptModel = next.harness === row.harness && next.model === row.model;
  return { machine, ...next, thinking: keptModel ? row.thinking : next.thinking };
}

export function rowAfterHarnessChange(
  row: RouteRow,
  harness: string,
  models: readonly UiCatalogueModel[],
): RouteRow {
  const first = modelsFor(row.machine, harness, models)[0];
  if (!first) return { ...row, harness, model: '', thinking: null };
  return { ...row, harness, model: first.id, thinking: defaultThinkingFor(harness, first.id, models) };
}

export function rowAfterModelChange(
  row: RouteRow,
  model: string,
  models: readonly UiCatalogueModel[],
): RouteRow {
  return { ...row, model, thinking: defaultThinkingFor(row.harness, model, models) };
}

/** A fresh row for "+ Add route": the first viable combination not already in the list. */
export function newRow(
  rows: readonly RouteRow[],
  machines: readonly RouteMachine[],
  models: readonly UiCatalogueModel[],
): RouteRow | undefined {
  const used = new Set(rows.map((row) => `${row.harness} ${row.model}`));
  for (const harness of harnessesFor(null, machines, models)) {
    for (const model of modelsFor(null, harness, models)) {
      if (used.has(`${harness} ${model.id}`)) continue;
      return { machine: null, harness, model: model.id, thinking: defaultThinkingFor(harness, model.id, models) };
    }
  }
  const first = firstViable(null, machines, models);
  return first ? { machine: null, ...first } : undefined;
}

function sameRow(a: RouteRow, b: RouteRow): boolean {
  return (
    sameName(a.machine, b.machine) &&
    a.harness === b.harness &&
    a.model === b.model &&
    (a.thinking ?? null) === (b.thinking ?? null)
  );
}

/** Indexes of rows that repeat an earlier row exactly. */
export function duplicateRows(rows: readonly RouteRow[]): Set<number> {
  const out = new Set<number>();
  rows.forEach((row, index) => {
    if (rows.slice(0, index).some((earlier) => sameRow(earlier, row))) out.add(index);
  });
  return out;
}

export function moveRow(rows: readonly RouteRow[], index: number, delta: -1 | 1): RouteRow[] {
  const target = index + delta;
  if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  const [moved] = next.splice(index, 1);
  if (moved) next.splice(target, 0, moved);
  return next;
}

/** One route in a phrase: `mediaservervm · Codex · gpt-5.6-luna · low`. */
export function routePhrase(row: RouteRow): string {
  const parts = [row.machine ?? 'any machine', harnessLabel(row.harness), row.model];
  if (row.thinking) parts.push(row.thinking);
  return parts.join(' · ');
}
